import { useSEO } from "@/hooks/useSEO";
import { FadeIn, Reveal } from "@/components/Motion";
import { AlertTriangle } from "lucide-react";

function LegalShell({ title, kicker, updated, children }) {
  return (
    <section className="pt-28 lg:pt-36 pb-24" data-testid="legal-page">
      <div className="max-w-3xl mx-auto px-5 lg:px-12">
        <FadeIn>
          <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
            <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
            {kicker}
          </p>
        </FadeIn>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 mb-4">{title}</h1>
        <FadeIn delay={0.3}>
          <p className="text-[13px] text-charcoal/60 mb-8">{updated}</p>
          <div className="flex gap-3 bg-peach/70 border border-taupe/30 rounded-2xl p-5 text-[13px] leading-relaxed text-deeptaupe mb-12" data-testid="legal-review-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <p>
              Testo segnaletico: i punti tra parentesi quadre sono segnaposto da completare e verificare con un
              legale prima della pubblicazione del sito.
            </p>
          </div>
        </FadeIn>
        <div className="space-y-10 text-[15px] leading-relaxed text-charcoal/80">{children}</div>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <Reveal>
      <div>
        <h2 className="font-display text-2xl lg:text-3xl text-charcoal mb-4">{title}</h2>
        <div className="space-y-3">{children}</div>
      </div>
    </Reveal>
  );
}

export function Privacy() {
  useSEO({
    title: "Privacy Policy — K Nutrition",
    description: "Informativa sul trattamento dei dati personali di K Nutrition — Dott.ssa Francesca Notarfrancesco, Salerno.",
    path: "/privacy",
  });

  return (
    <LegalShell
      title="Privacy Policy"
      kicker="Informative"
      updated="Ultimo aggiornamento: [da completare]"
    >
      <Section title="Titolare del trattamento">
        <p>
          K Nutrition — Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista
          <br />
          Via Santi Martiri Salernitani 15, Salerno, Italia
          <br />
          Email: knutrition.studio@gmail.com · Telefono: +39 379 316 5184
          <br />
          [P.IVA e dati anagrafici completi del titolare — da completare]
        </p>
      </Section>

      <Section title="Dati raccolti attraverso il sito">
        <p>
          Questo sito non utilizza moduli che inviano dati a server di terze parti. La richiesta di appuntamento
          viene inviata dall'utente direttamente tramite WhatsApp: al titolare vengono comunicati esclusivamente il
          nome, il servizio scelto e le preferenze di data e orario indicate dall'utente.
        </p>
        <p>
          Ti chiediamo di non inserire nella richiesta informazioni riguardanti la tua salute: eventuali
          informazioni cliniche saranno raccolte direttamente dal titolare durante la visita, negli appositi
          modulistiche e nel rispetto della normativa.
        </p>
      </Section>

      <Section title="Base giuridica e finalità">
        <p>
          I dati comunicati volontariamente tramite WhatsApp sono trattati dal titolare al fine esclusivo di
          gestire la richiesta di appuntamento e l'eventuale rapporto professionale successivo (base giuridica:
          esecuzione di misure precontrattuali e contrattuali, art. 6 lett. b GDPR).
        </p>
      </Section>

      <Section title="Conservazione dei dati">
        <p>[Periodo di conservazione — da completare, es. tempi e modalità di conservazione delle cartelle cliniche.]</p>
      </Section>

      <Section title="Diritti dell'interessato">
        <p>
          Puoi esercitare in ogni momento i diritti previsti dal Regolamento UE 2016/679 (accesso, rettifica,
          cancellazione, limitazione, opposizione, portabilità) scrivendo a knutrition.studio@gmail.com. Hai
          inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali.
        </p>
      </Section>

      <Section title="Cookie">
        <p>
          Per informazioni sui cookie utilizzati da questo sito consulta la{" "}
          <a href="/cookie-policy" className="text-deeptaupe underline underline-offset-2">Cookie Policy</a>.
        </p>
      </Section>
    </LegalShell>
  );
}

export function CookiePolicy() {
  useSEO({
    title: "Cookie Policy — K Nutrition",
    description: "Cookie Policy del sito di K Nutrition — Dott.ssa Francesca Notarfrancesco, Salerno.",
    path: "/cookie-policy",
  });

  return (
    <LegalShell
      title="Cookie Policy"
      kicker="Informative"
      updated="Ultimo aggiornamento: [da completare]"
    >
      <Section title="Cosa sono i cookie">
        <p>
          I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente. Vengono
          utilizzati per garantire il funzionamento del sito e per raccogliere informazioni di navigazione.
        </p>
      </Section>

      <Section title="Cookie utilizzati da questo sito">
        <p>
          Questo sito <strong>non utilizza cookie di profilazione, analytics o tracciamento</strong>. Non vengono
          caricati strumenti statistici o pubblicitari.
        </p>
        <p>
          Sono utilizzati esclusivamente cookie tecnici essenziali per il funzionamento dell'area riservata di
          amministrazione (sessione di accesso dell'amministratore). Se visiti il sito solo come utente, nessun
          cookie viene impostato nel tuo browser da questo sito.
        </p>
      </Section>

      <Section title="Servizi di terze parti">
        <p>
          I pulsanti di contatto verso WhatsApp e Instagram, nonché la mappa Google Maps integrata nella pagina
          contatti, possono comportare il trasferimento di dati verso i fornitori dei relativi servizi quando
          scegli di interagirvi. Per i trattamenti effettuati da questi fornitori consulta le rispettive
          informative: [link alle informative di WhatsApp, Meta e Google — da completare].
        </p>
      </Section>

      <Section title="Gestione delle preferenze">
        <p>
          Puoi gestire o eliminare i cookie dalle impostazioni del tuo browser in qualsiasi momento. La
          disabilitazione dei cookie tecnici potrebbe compromettere il funzionamento dell'area amministrativa.
        </p>
      </Section>
    </LegalShell>
  );
}
